import React from 'react'
import bowser from 'bowser'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { includes, isFunction } from 'lodash-es'
import { FormattedMessage } from 'react-intl'

import Modal from 'antd/lib/modal'
import 'antd/lib/modal/style/css'

import { Button as ButtonComponent, AjaxButtonComponent } from '..'

import './ModalComponent.scss'

const getFooter = (props) => {
  const {
    showFooter,
    footer,
    onCancel,
    onOk,
    cancelText,
    okText,
    okButtonState,
    cancelButtonProps,
    okButtonProps,
  } = props

  if (showFooter) {
    if (footer) {
      return footer
    }

    return [
      <ButtonComponent
        key="back"
        onClick={onCancel}
        color="grey"
        outlined
        disabled={includes(['loading', 'success'], okButtonState)}
        {...cancelButtonProps}
      >
        {cancelText || <FormattedMessage defaultMessage="Cancel" />}
      </ButtonComponent>,
      <AjaxButtonComponent
        key="submit"
        onClick={onOk}
        state={okButtonState}
        {...okButtonProps}
      >
        {okText || <FormattedMessage defaultMessage="Ok" />}
      </AjaxButtonComponent>,
    ]
  }

  return null
}

// Add class "is-modal-open" to body when a modal is open
// it is used to force hide the video popover when we open a modal
// otherwise it stays open on Safari after opening the modal
const { body } = document
const isSafari = bowser.safari

const addClassToBody = (visible) => {
  if (visible && !body.classList.contains('is-modal-open')) {
    body.classList.add('is-modal-open')
  }
}

const removeClassFromBody = () => {
  if (body.classList.contains('is-modal-open')) {
    // For Safari we remove the class after some time because
    // otherwise the popover jumps for a second if the mouse is on the same position
    // In the other browsers we need to remove it immediately otherwise the popover is not shown
    // if we are on the same position
    if (isSafari) {
      setTimeout(() => {
        body.classList.remove('is-modal-open')
      }, 150) // magic number -- it seems enough time so the popover do not jump
    } else {
      body.classList.remove('is-modal-open')
    }
  }
}

const afterClose = (props) => {
  removeClassFromBody()

  if (isFunction(props.afterClose)) {
    props.afterClose()
  }
}

const getMaskStyle = (overlayTransparency, customMaskStyle) => {
  let maskStyle = {
    backgroundColor: '#373737',
    transition: '0.5s linear opacity',
    ...customMaskStyle,
  }

  if (overlayTransparency === 'opaque') {
    maskStyle = {
      ...maskStyle,
      opacity: 1,
    }
  } else if (overlayTransparency === 'transparent') {
    maskStyle = {
      ...maskStyle,
      opacity: 0,
    }
  } else {
    maskStyle = {
      ...maskStyle,
      opacity: 0.65,
    }
  }

  return maskStyle
}

const ModalComponent = ({ overlayTransparency, ...props }) => {
  const { visible, noPadding, wrapClassName, maskStyle } = props

  addClassToBody(visible)

  const wrapClassNames = classnames(
    'modal-component antd-component',
    { 'no-padding': noPadding },
    wrapClassName
  )

  const newProps = { ...props }
  newProps.maskStyle = getMaskStyle(overlayTransparency, maskStyle)

  return (
    <Modal
      {...newProps}
      footer={getFooter(props)}
      wrapClassName={wrapClassNames}
      afterClose={() => afterClose(props)}
    />
  )
}

ModalComponent.propTypes = {
  visible: PropTypes.bool,
  noPadding: PropTypes.bool,
  showFooter: PropTypes.bool,
  footer: PropTypes.node,
  onCancel: PropTypes.func,
  onOk: PropTypes.func,
  cancelText: PropTypes.string,
  cancelButtonProps: PropTypes.object,
  okText: PropTypes.string,
  okButtonProps: PropTypes.object,
  afterClose: PropTypes.func,
  okButtonState: PropTypes.oneOf(['idle', 'loading', 'success', 'failure']),
  wrapClassName: PropTypes.string,
  overlayTransparency: PropTypes.oneOf(['transparent', 'default', 'opaque']),
  maskStyle: PropTypes.object,
}

ModalComponent.defaultProps = {
  okButtonState: 'idle',
  okButtonProps: {},
  cancelButtonProps: {},
  overlayTransparency: 'default',
}

export default ModalComponent
